// src/components/admin/StaffManager.js  — NEW PAGE (Admin only)
// Manage staff & admin accounts, set roles, reset passwords
import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import {
  PlusIcon, TrashIcon, KeyIcon, ShieldCheckIcon, UserIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_API_URL || 'https://freshhillsmilk.in/api';

const authH = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}`,
});

// ── Permission matrix display ─────────────────────────────────────────────
const PERM_MATRIX = [
  { label: 'Dashboard',        admin: true,  staff: true  },
  { label: 'Products (edit)',  admin: true,  staff: false },
  { label: 'Fridges (edit)',   admin: true,  staff: false },
  { label: 'Assign Products',  admin: true,  staff: false },
  { label: 'Stock Monitor',    admin: true,  staff: true  },
  { label: 'Sales Report',     admin: true,  staff: false },
  { label: 'Refill Schedule',  admin: true,  staff: true  },
  { label: 'Orders (view)',    admin: true,  staff: true  },
  { label: 'Customers',        admin: true,  staff: false },
  { label: 'Staff Manager',    admin: true,  staff: false },
];

const StaffManager = () => {
  const { admin: me, isAdmin } = useAuth();
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPwModal, setShowPwModal] = useState(null); // user obj
  const [saving,    setSaving]    = useState(false);
  const [form, setForm] = useState({ username:'', password:'', role:'staff', email:'' });
  const [pwForm, setPwForm] = useState({ password:'', confirm:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/admin/staff`, { headers: authH() });
      const data = await res.json();
      if (data.success) setUsers(data.users || []);
      else toast.error(data.message || 'Failed to load users');
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) { toast.error('Fill all required fields'); return; }
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      const res  = await fetch(`${API}/admin/staff`, {
        method: 'POST', headers: authH(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${form.role === 'admin' ? 'Admin' : 'Staff'} account created`);
        setShowModal(false);
        setForm({ username:'', password:'', role:'staff', email:'' });
        load();
      } else {
        toast.error(data.message || 'Failed to create user');
      }
    } catch { toast.error('Failed to create user'); }
    finally  { setSaving(false); }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete "${user.username}"? This cannot be undone.`)) return;
    try {
      const res  = await fetch(`${API}/admin/staff/${user.id}`, { method:'DELETE', headers: authH() });
      const data = await res.json();
      if (data.success) { toast.success('User deleted'); load(); }
      else toast.error(data.message || 'Cannot delete this user');
    } catch { toast.error('Delete failed'); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    if (pwForm.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      const res  = await fetch(`${API}/admin/staff/${showPwModal.id}/password`, {
        method: 'PUT', headers: authH(),
        body: JSON.stringify({ password: pwForm.password }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Password updated');
        setShowPwModal(null);
        setPwForm({ password:'', confirm:'' });
      } else {
        toast.error(data.message || 'Failed to update password');
      }
    } catch { toast.error('Failed to update password'); }
    finally  { setSaving(false); }
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Nunito',sans-serif; }
    @keyframes spin { to { transform:rotate(360deg); } }
    @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }

    .sm-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:2rem; flex-wrap:wrap; gap:1rem; }
    .sm-title  { font-size:1.6rem; font-weight:900; color:#111827; }
    .sm-sub    { font-size:0.88rem; color:#64748b; font-weight:600; margin-top:0.15rem; }

    .sm-add-btn {
      display:flex; align-items:center; gap:0.5rem; padding:0.6rem 1.4rem; border-radius:9999px;
      background:linear-gradient(135deg,#10b981,#059669); color:white; font-weight:800; font-size:0.9rem;
      border:none; cursor:pointer; box-shadow:0 4px 14px rgba(16,185,129,0.3); transition:all 0.3s; font-family:'Nunito',sans-serif;
    }
    .sm-add-btn svg { width:1rem; height:1rem; }
    .sm-add-btn:hover { transform:translateY(-2px); box-shadow:0 8px 20px rgba(16,185,129,0.35); }

    /* Stats row */
    .sm-stats { display:grid; grid-template-columns:repeat(3,1fr); gap:1.25rem; margin-bottom:2rem; }
    .sm-stat-card { background:white; border-radius:1.25rem; padding:1.25rem 1.5rem;
                    border:1.5px solid; box-shadow:0 3px 12px rgba(0,0,0,0.04); text-align:center; }
    .sm-stat-num  { font-size:2rem; font-weight:900; }
    .sm-stat-lbl  { font-size:0.78rem; font-weight:700; color:#64748b; margin-top:0.15rem; }

    /* Layout: user table + matrix */
    .sm-layout { display:grid; grid-template-columns:1fr; gap:1.5rem; }
    @media(min-width:1024px) { .sm-layout { grid-template-columns:1.5fr 1fr; } }

    .sm-card { background:white; border-radius:1.5rem; overflow:hidden; border:1px solid #f1f5f9;
               box-shadow:0 4px 16px rgba(0,0,0,0.04); animation:fadeUp 0.35s ease both; }
    .sm-card-title { font-size:1rem; font-weight:800; color:#111827; padding:1.25rem 1.5rem;
                     border-bottom:1px solid #f1f5f9; display:flex; align-items:center; gap:0.5rem; }
    .sm-card-title svg { width:1rem; height:1rem; color:#059669; }

    /* User list */
    .sm-user-item { display:flex; align-items:center; gap:1rem; padding:1rem 1.5rem;
                    border-bottom:1px solid #f8fafc; transition:background 0.2s; flex-wrap:wrap; }
    .sm-user-item:last-child { border-bottom:none; }
    .sm-user-item:hover { background:#fafbfc; }

    .sm-user-av { width:40px; height:40px; border-radius:50%; flex-shrink:0;
                  display:flex; align-items:center; justify-content:center;
                  font-weight:900; font-size:0.95rem; color:white; }
    .sm-user-info  { flex:1; min-width:0; }
    .sm-user-name  { font-weight:800; color:#111827; font-size:0.9rem; }
    .sm-user-email { font-size:0.75rem; color:#94a3b8; font-weight:600; }
    .sm-user-meta  { font-size:0.7rem; color:#64748b; margin-top:0.15rem; font-weight:600; }

    .sm-role-badge { font-size:0.68rem; font-weight:800; padding:0.22rem 0.65rem;
                     border-radius:9999px; border:1.5px solid; flex-shrink:0; letter-spacing:0.3px; }

    .sm-user-actions { display:flex; align-items:center; gap:0.45rem; flex-shrink:0; }
    .sm-pw-btn { display:flex; align-items:center; gap:0.3rem; padding:0.35rem 0.75rem;
                 border-radius:9999px; background:#eff6ff; color:#2563eb; border:1.5px solid #bfdbfe;
                 font-weight:700; font-size:0.72rem; cursor:pointer; font-family:'Nunito',sans-serif; transition:all 0.2s; }
    .sm-pw-btn svg { width:0.75rem; height:0.75rem; }
    .sm-pw-btn:hover { background:#2563eb; color:white; border-color:#2563eb; }
    .sm-del-btn { width:30px; height:30px; border-radius:50%; background:#fff1f2; color:#dc2626;
                  border:1.5px solid #fecdd3; cursor:pointer; display:flex; align-items:center;
                  justify-content:center; transition:all 0.2s; flex-shrink:0; }
    .sm-del-btn svg { width:0.78rem; height:0.78rem; }
    .sm-del-btn:hover { background:#dc2626; color:white; border-color:#dc2626; }
    .sm-del-btn:disabled { opacity:0.35; cursor:not-allowed; }
    .sm-me-tag { font-size:0.62rem; background:#ecfdf5; color:#059669; padding:0.1rem 0.4rem;
                 border-radius:9999px; font-weight:800; flex-shrink:0; }

    /* Permission matrix */
    .sm-matrix { width:100%; border-collapse:collapse; font-size:0.78rem; }
    .sm-mth { padding:0.65rem 1rem; font-size:0.68rem; font-weight:800; color:#64748b;
              text-transform:uppercase; letter-spacing:0.5px; border-bottom:1px solid #f1f5f9;
              background:#f8fafc; text-align:center; }
    .sm-mth:first-child { text-align:left; }
    .sm-mtd { padding:0.6rem 1rem; border-bottom:1px solid #f8fafc; vertical-align:middle; text-align:center; }
    .sm-mtd:first-child { text-align:left; font-weight:700; color:#374151; font-size:0.8rem; }
    .sm-mtr:last-child .sm-mtd { border-bottom:none; }

    /* Modal */
    .sm-modal-bg { position:fixed; inset:0; background:rgba(15,23,42,0.5); display:flex;
                   align-items:center; justify-content:center; z-index:200; padding:1rem; backdrop-filter:blur(4px); }
    .sm-modal    { background:white; border-radius:1.75rem; width:100%; max-width:440px;
                   max-height:90vh; display:flex; flex-direction:column;
                   box-shadow:0 25px 60px -10px rgba(0,0,0,0.25); overflow:hidden; animation:fadeUp 0.3s ease both; }
    .sm-modal-hdr { padding:1.5rem; display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
    .sm-modal-hdr h3 { font-size:1.1rem; font-weight:900; color:white; }
    .sm-modal-close { width:32px; height:32px; border-radius:50%; background:rgba(255,255,255,0.2);
                      border:none; color:white; cursor:pointer; display:flex; align-items:center;
                      justify-content:center; font-size:1rem; }
    .sm-modal-body  { padding:1.5rem; overflow-y:auto; flex:1; }
    .sm-modal-footer { display:flex; justify-content:flex-end; gap:0.75rem; padding:1.25rem 1.5rem;
                       border-top:1px solid #f1f5f9; flex-shrink:0; }
    .sm-field  { margin-bottom:1.1rem; }
    .sm-label  { display:block; font-size:0.82rem; font-weight:700; color:#374151; margin-bottom:0.45rem; }
    .sm-input, .sm-select {
      width:100%; padding:0.65rem 1rem; border:1.5px solid #e5e7eb; border-radius:0.875rem;
      font-size:0.9rem; font-family:'Nunito',sans-serif; font-weight:600; color:#111827;
      background:#f9fafb; outline:none; transition:all 0.2s;
    }
    .sm-input:focus, .sm-select:focus { border-color:#059669; background:white; box-shadow:0 0 0 3px rgba(5,150,105,0.1); }
    .sm-cancel-btn { padding:0.6rem 1.4rem; border-radius:9999px; background:#f8fafc;
                     border:1.5px solid #e2e8f0; color:#64748b; font-weight:700; cursor:pointer;
                     font-family:'Nunito',sans-serif; transition:all 0.2s; }
    .sm-cancel-btn:hover { border-color:#e11d48; color:#e11d48; }
    .sm-save-btn { padding:0.6rem 1.75rem; border-radius:9999px; color:white;
                   font-weight:800; border:none; cursor:pointer; font-family:'Nunito',sans-serif;
                   transition:all 0.3s; box-shadow:0 4px 14px rgba(16,185,129,0.3); }
    .sm-save-btn:hover    { transform:translateY(-1px); }
    .sm-save-btn:disabled { opacity:0.6; cursor:not-allowed; transform:none; }

    /* role toggle */
    .sm-role-toggle { display:grid; grid-template-columns:1fr 1fr; gap:0.5rem; }
    .sm-role-opt { border-radius:0.875rem; border:2px solid #e5e7eb; padding:0.75rem;
                   cursor:pointer; transition:all 0.2s; }
    .sm-role-opt.sel-admin { border-color:#059669; background:#f0fdf4; }
    .sm-role-opt.sel-staff { border-color:#2563eb; background:#eff6ff; }
    .sm-role-opt-title { font-size:0.8rem; font-weight:800; margin-bottom:0.2rem; }
    .sm-role-opt-desc  { font-size:0.7rem; color:#64748b; font-weight:600; line-height:1.4; }

    .sm-spinner { width:2.5rem; height:2.5rem; border:3px solid #f3f4f6; border-top-color:#059669;
                  border-radius:50%; animation:spin 0.7s linear infinite; margin:4rem auto; }
    .sm-empty { padding:3rem 2rem; text-align:center; color:#94a3b8; font-weight:700; }
  `;

  const admins = users.filter(u => u.role === 'admin');
  const staff  = users.filter(u => u.role === 'staff');

  if (!isAdmin) {
    return (
      <AdminLayout>
        <style>{css}</style>
        <div style={{ textAlign:'center', padding:'4rem 2rem', color:'#94a3b8' }}>
          <ShieldCheckIcon style={{ width:'3rem', height:'3rem', margin:'0 auto 1rem', opacity:0.35 }} />
          <div style={{ fontWeight:800, fontSize:'1.1rem', color:'#374151' }}>Admin Access Only</div>
          <div style={{ fontSize:'0.85rem', marginTop:'0.5rem' }}>Staff Manager is restricted to Admin accounts.</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <style>{css}</style>

      <div className="sm-header">
        <div>
          <div className="sm-title">🛡️ Staff Manager</div>
          <div className="sm-sub">Manage admin and staff accounts & permissions</div>
        </div>
        <div style={{ display:'flex', gap:'0.65rem' }}>
          <button className="sm-add-btn" style={{ background:'none', border:'1.5px solid #e5e7eb', color:'#374151', boxShadow:'none' }}
            onClick={load} disabled={loading}>
            <ArrowPathIcon style={loading ? { animation:'spin 1s linear infinite' } : {}} />
            Refresh
          </button>
          <button className="sm-add-btn" onClick={() => setShowModal(true)}>
            <PlusIcon /> Add User
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="sm-stats">
        <div className="sm-stat-card" style={{ borderColor:'#a7f3d0' }}>
          <div className="sm-stat-num" style={{ color:'#059669' }}>{admins.length}</div>
          <div className="sm-stat-lbl">Admins</div>
        </div>
        <div className="sm-stat-card" style={{ borderColor:'#bfdbfe' }}>
          <div className="sm-stat-num" style={{ color:'#2563eb' }}>{staff.length}</div>
          <div className="sm-stat-lbl">Staff</div>
        </div>
        <div className="sm-stat-card" style={{ borderColor:'#ddd6fe' }}>
          <div className="sm-stat-num" style={{ color:'#7c3aed' }}>{users.length}</div>
          <div className="sm-stat-lbl">Total Users</div>
        </div>
      </div>

      <div className="sm-layout">
        {/* User list */}
        <div className="sm-card">
          <div className="sm-card-title">
            <UserIcon /> All Users
          </div>
          {loading ? <div className="sm-spinner" /> :
           users.length === 0 ? <div className="sm-empty">No users found</div> :
           users.map(user => {
            const isMe  = user.id === me?.id;
            const isAdm = user.role === 'admin';
            return (
              <div key={user.id} className="sm-user-item">
                <div className="sm-user-av"
                  style={{ background: isAdm ? 'linear-gradient(135deg,#059669,#0891b2)' : 'linear-gradient(135deg,#2563eb,#7c3aed)' }}>
                  {user.username[0].toUpperCase()}
                </div>
                <div className="sm-user-info">
                  <div className="sm-user-name" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
                    {user.username}
                    {isMe && <span className="sm-me-tag">You</span>}
                  </div>
                  {user.email && <div className="sm-user-email">{user.email}</div>}
                  <div className="sm-user-meta">
                    Created: {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN') : '—'}
                  </div>
                </div>
                <span className="sm-role-badge"
                  style={isAdm
                    ? { color:'#059669', background:'#ecfdf5', borderColor:'#a7f3d0' }
                    : { color:'#2563eb', background:'#eff6ff', borderColor:'#bfdbfe' }}>
                  {isAdm ? '👑 Admin' : '👤 Staff'}
                </span>
                <div className="sm-user-actions">
                  <button className="sm-pw-btn"
                    onClick={() => { setShowPwModal(user); setPwForm({ password:'', confirm:'' }); }}>
                    <KeyIcon /> Password
                  </button>
                  <button className="sm-del-btn"
                    disabled={isMe || isAdm}
                    title={isMe ? "Can't delete yourself" : isAdm ? "Can't delete admin" : 'Delete'}
                    onClick={() => handleDelete(user)}>
                    <TrashIcon />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Permission matrix */}
        <div className="sm-card">
          <div className="sm-card-title">
            <ShieldCheckIcon /> Permission Matrix
          </div>
          <div style={{ padding:'0.5rem 0', overflowX:'auto' }}>
            <table className="sm-matrix">
              <thead>
                <tr>
                  <th className="sm-mth">Feature</th>
                  <th className="sm-mth" style={{ color:'#059669' }}>Admin</th>
                  <th className="sm-mth" style={{ color:'#2563eb' }}>Staff</th>
                </tr>
              </thead>
              <tbody>
                {PERM_MATRIX.map(p => (
                  <tr key={p.label} className="sm-mtr">
                    <td className="sm-mtd">{p.label}</td>
                    <td className="sm-mtd">
                      <span style={{ fontSize:'1rem' }}>{p.admin ? '✅' : '❌'}</span>
                    </td>
                    <td className="sm-mtd">
                      <span style={{ fontSize:'1rem' }}>{p.staff ? '✅' : '🔒'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Add User Modal ── */}
      {showModal && (
        <div className="sm-modal-bg" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="sm-modal">
            <div className="sm-modal-hdr"
              style={{ background: form.role === 'admin'
                ? 'linear-gradient(135deg,#059669,#0891b2)'
                : 'linear-gradient(135deg,#2563eb,#7c3aed)' }}>
              <h3>{form.role === 'admin' ? '👑 Add Admin' : '👤 Add Staff'}</h3>
              <button className="sm-modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleCreate} style={{ display:'contents' }}>
              <div className="sm-modal-body">

                {/* Role toggle */}
                <div className="sm-field">
                  <label className="sm-label">Role *</label>
                  <div className="sm-role-toggle">
                    <div className={`sm-role-opt ${form.role==='admin'?'sel-admin':''}`}
                      onClick={() => setForm({...form, role:'admin'})}>
                      <div className="sm-role-opt-title" style={{ color:'#059669' }}>👑 Admin</div>
                      <div className="sm-role-opt-desc">Full access. Can manage staff, products, fridges, and all settings.</div>
                    </div>
                    <div className={`sm-role-opt ${form.role==='staff'?'sel-staff':''}`}
                      onClick={() => setForm({...form, role:'staff'})}>
                      <div className="sm-role-opt-title" style={{ color:'#2563eb' }}>👤 Staff</div>
                      <div className="sm-role-opt-desc">Limited access. Can view orders, manage stock & refill schedules.</div>
                    </div>
                  </div>
                </div>

                <div className="sm-field">
                  <label className="sm-label">Username *</label>
                  <input className="sm-input" placeholder="e.g. john_staff"
                    value={form.username}
                    onChange={e => setForm({...form, username: e.target.value})} required />
                </div>
                <div className="sm-field">
                  <label className="sm-label">Password * (min 6 characters)</label>
                  <input className="sm-input" type="password" placeholder="Set a strong password"
                    value={form.password}
                    onChange={e => setForm({...form, password: e.target.value})} required />
                </div>
                <div className="sm-field">
                  <label className="sm-label">Email (optional)</label>
                  <input className="sm-input" type="email" placeholder="staff@freshhills.in"
                    value={form.email}
                    onChange={e => setForm({...form, email: e.target.value})} />
                </div>
              </div>
              <div className="sm-modal-footer">
                <button type="button" className="sm-cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="sm-save-btn" disabled={saving}
                  style={{ background: form.role==='admin'
                    ? 'linear-gradient(135deg,#10b981,#059669)'
                    : 'linear-gradient(135deg,#2563eb,#7c3aed)' }}>
                  {saving ? '⏳ Creating…' : `✅ Create ${form.role === 'admin' ? 'Admin' : 'Staff'}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Change Password Modal ── */}
      {showPwModal && (
        <div className="sm-modal-bg" onClick={e => e.target === e.currentTarget && setShowPwModal(null)}>
          <div className="sm-modal">
            <div className="sm-modal-hdr" style={{ background:'linear-gradient(135deg,#374151,#1e293b)' }}>
              <h3>🔑 Change Password — {showPwModal.username}</h3>
              <button className="sm-modal-close" onClick={() => setShowPwModal(null)}>✕</button>
            </div>
            <form onSubmit={handleChangePassword} style={{ display:'contents' }}>
              <div className="sm-modal-body">
                <div className="sm-field">
                  <label className="sm-label">New Password *</label>
                  <input className="sm-input" type="password" placeholder="Min 6 characters"
                    value={pwForm.password}
                    onChange={e => setPwForm({...pwForm, password: e.target.value})} required />
                </div>
                <div className="sm-field">
                  <label className="sm-label">Confirm Password *</label>
                  <input className="sm-input" type="password" placeholder="Repeat new password"
                    value={pwForm.confirm}
                    onChange={e => setPwForm({...pwForm, confirm: e.target.value})} required />
                </div>
              </div>
              <div className="sm-modal-footer">
                <button type="button" className="sm-cancel-btn" onClick={() => setShowPwModal(null)}>Cancel</button>
                <button type="submit" className="sm-save-btn" disabled={saving}
                  style={{ background:'linear-gradient(135deg,#374151,#1e293b)' }}>
                  {saving ? '⏳ Saving…' : '🔑 Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default StaffManager;